'use client';
import React, { useState } from 'react';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { CheckCircle, XCircle, Dumbbell } from 'lucide-react';

interface ExerciseItem {
  id: string;
  account: { fr: string; ru: string };
  correctAnswer: 'debit' | 'credit';
}

interface ExerciseStepData {
  type: 'exercise';
  title: { fr: string; ru: string };
  question: { fr: string; ru: string };
  items: ExerciseItem[];
}

interface Props {
  step: ExerciseStepData;
  language: Language;
  onCheck: (answers: Record<string, string>) => void;
  checked: boolean;
  userAnswers: Record<string, string>;
}

export default function ExerciseStep({ step, language, onCheck, checked, userAnswers }: Props) {
  const [answers, setAnswers] = useState<Record<string, string>>(userAnswers);

  const handleSelect = (itemId: string, value: 'debit' | 'credit') => {
    if (checked) return;
    setAnswers((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleCheck = () => {
    if (Object.keys(answers).length < step.items.length) return;
    onCheck(answers);
  };

  const allAnswered = Object.keys(answers).length === step.items.length;

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
          <Dumbbell size={20} className="text-primary" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground font-500">{t(language, 'lesson_exercise')}</p>
          <h2 className="text-lg font-700 text-foreground">{step.title[language]}</h2>
        </div>
      </div>

      <div className="bg-secondary rounded-xl p-4 mb-5 border border-border">
        <p className="text-sm text-foreground font-500">{step.question[language]}</p>
      </div>

      {/* Exercise items */}
      <div className="space-y-3">
        {step.items.map((item) => {
          const userAnswer = answers[item.id];
          const isCorrect = userAnswer === item.correctAnswer;

          return (
            <div
              key={`exercise-item-${item.id}`}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                checked
                  ? isCorrect
                    ? 'bg-green-50 border-green-300' :'bg-red-50 border-red-300' :'bg-card border-border'
              }`}
            >
              {/* Account name */}
              <div className="flex-1">
                <p className="text-sm font-600 text-foreground">{item.account[language]}</p>
                {checked && (
                  <p className={`text-xs font-500 mt-0.5 ${isCorrect ? 'text-success' : 'text-danger'}`}>
                    {isCorrect
                      ? t(language, 'lesson_correct')
                      : `${t(language, 'lesson_incorrect')} — ${item.correctAnswer === 'debit' ? t(language, 'lesson_debit') : t(language, 'lesson_credit')}`}
                  </p>
                )}
              </div>

              {/* Debit / Credit buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSelect(item.id, 'debit')}
                  disabled={checked}
                  className={`px-4 py-2 rounded-xl text-sm font-600 border transition-all duration-150 active:scale-95 disabled:cursor-default ${
                    userAnswer === 'debit'
                      ? checked
                        ? item.correctAnswer === 'debit' ?'bg-success text-white border-success' :'bg-danger text-white border-danger' :'bg-primary text-white border-primary' :'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
                  }`}
                >
                  {t(language, 'lesson_debit')}
                </button>
                <button
                  onClick={() => handleSelect(item.id, 'credit')}
                  disabled={checked}
                  className={`px-4 py-2 rounded-xl text-sm font-600 border transition-all duration-150 active:scale-95 disabled:cursor-default ${
                    userAnswer === 'credit'
                      ? checked
                        ? item.correctAnswer === 'credit' ?'bg-success text-white border-success' :'bg-danger text-white border-danger' :'bg-primary text-white border-primary' :'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
                  }`}
                >
                  {t(language, 'lesson_credit')}
                </button>

                {checked && (
                  <div className="ml-1">
                    {isCorrect ? (
                      <CheckCircle size={18} className="text-success" />
                    ) : (
                      <XCircle size={18} className="text-danger" />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Check button */}
      {!checked && (
        <button
          onClick={handleCheck}
          disabled={!allAnswered}
          className="mt-5 w-full py-3 bg-primary text-white font-600 rounded-xl text-sm hover:bg-primary/90 transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t(language, 'lesson_check')}
        </button>
      )}

      {checked && (
        <div className="mt-5 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle size={20} className="text-success flex-shrink-0" />
          <p className="text-sm font-600 text-success">
            {language === 'fr' ?'Exercice terminé! Cliquez sur Suivant pour continuer.' :'Упражнение завершено! Нажмите Далее для продолжения.'}
          </p>
        </div>
      )}
    </div>
  );
}